import React from 'react'
import Image, { ImageLoader } from 'next/image'
import { setImageParams } from '../../utils/helpers/helpers'
import styles from './InterstitialPopup.module.scss'
import { Logo } from '@awaze/design-system.components.logo'
import { Heading, Text } from '@awaze/design-system.components.text'
import { renderContentfulRichText } from '@awaze/mfe-common.lib.contentful/dist/api/utils/render-richtext'
import { richTextOptions } from '../../lib/contentful/contentful.utils'
import { Button } from '@awaze/design-system.components.button'
import { IInterstitialPageFields } from '../../../contentful/types/contentful-modules'
import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { useRouter } from 'next/router'

interface InterstitialProps {
  brand: Brand
  interstitialPageData: IInterstitialPageFields
}

export const InterstitialPopup: React.FC<InterstitialProps> = ({
  interstitialPageData,
  brand,
}) => {
  const router = useRouter()
  const [showInterstitial, setShowInterstitial] = React.useState(true)

  if (
    !router.asPath.includes('age_migrated_brand') ||
    !interstitialPageData ||
    !showInterstitial
  ) {
    return null
  }

  const data = interstitialPageData
  const customLoader: ImageLoader = ({ src }) =>
    setImageParams(src, {
      q: '80',
      fm: 'webp',
    })

  return (
    <div
      className={styles.container}
      id="interstitial-container"
      data-testid="interstitial-container"
    >
      <div className={styles.boundingBox}>
        <Logo brand={brand} variant="reversed" height={46} />

        <div className={styles.dialog}>
          {data.brandImage.fields.file && (
            <div className={styles.brandLogo}>
              <Image
                src={data.brandImage.fields.file?.url}
                alt={data.brandImage.fields.description}
                fill={true}
                style={{ objectFit: 'contain' }}
                loader={customLoader}
              />
            </div>
          )}
          <Heading sizes={200} as={'h2'} mb={24}>
            {data?.dialogTitle ??
              `This brand was acquired by ${brand} click to continue`}
          </Heading>
          <Text as={'div'} type={'mid'}>
            {
              renderContentfulRichText(
                data.dialogText,
                richTextOptions,
              ) as React.ReactNode
            }
          </Text>
          <Button
            as={'a'}
            text={data?.buttonText ?? `Continue to ${brand}`}
            size="medium"
            className={styles.cta}
            variant={
              data?.buttonOptions?.fields?.variant
                ? data.buttonOptions.fields?.variant
                : 'solid'
            }
            colourScheme={
              data?.buttonOptions?.fields?.colorScheme &&
              data?.buttonOptions?.fields?.colorScheme !== 'none'
                ? data.buttonOptions.fields?.colorScheme
                : 'brand-primary'
            }
            onClick={() => {
              setShowInterstitial(false)
            }}
          />
        </div>
      </div>
      <div className={styles.imageParentWrapper}>
        <div className={styles.imageWrapper}>
          <div className={styles.desktopImage}>
            <Image
              src={
                data.backgroundImage.fields.file
                  ? data.backgroundImage.fields.file?.url
                  : '/images/interstitial_desktop_default.png'
              }
              alt={data.backgroundImage.fields?.description ?? ''}
              fill={true}
              style={{ objectFit: 'cover' }}
              loader={customLoader}
            />
          </div>
          <div className={styles.mobileImage}>
            <Image
              src={
                data.backgroundImageMobile.fields.file
                  ? data.backgroundImageMobile.fields.file?.url
                  : '/images/interstitial_mobile_default.png'
              }
              alt={data.backgroundImageMobile.fields?.description ?? ''}
              fill={true}
              style={{ objectFit: 'cover' }}
              loader={customLoader}
            />
          </div>
        </div>
      </div>
      <Logo
        className={styles.partOfAwazeLogo}
        brand="partofawaze"
        height={46}
      />
    </div>
  )
}
